import { useQuery } from "@tanstack/react-query";

import {
  ChangeEvent,
  DragEvent,
  FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaUpload } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";

import { Button } from "@/components/ui";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  SearchSelect,
  SearchSelectContent,
  SearchSelectItem,
  SearchSelectTrigger,
  SearchSelectValue,
} from "@/components/ui/select/search-select";
import { SmartDateInput } from "@/components/ui/smart-date-input";

import { useUser } from "@/contexts/user-provider";

import { pickedDay } from "@/lib/clinic-clock";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { useToast } from "@/lib/use-toast";

import { DocumentSummary, ID_DOCUMENT_TYPE_NAME } from "@/types/document";

import ScanControls from "./scan-controls";
import { useUploadDocumentMutation } from "@/api/provider/user/patient/document-mutation";
import {
  documentTypeQueryOptions,
  useCreateDocumentTypeMutation,
} from "@/api/provider/user/patient/document-type-query";

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / (1024 * 1024);

interface DocumentFormData {
  title: string;
  docType: string;
  file: File | null;
  expiryDate: Date | null;
}

interface DocumentUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  onSuccess?: (document: DocumentSummary) => void;
}

export default function DocumentUpload({
  open,
  onOpenChange,
  patientId,
  onSuccess,
}: DocumentUploadProps) {
  const { toast } = useToast();
  const { currentUser } = useUser();
  const uploadMutation = useUploadDocumentMutation();
  const createTypeMutation = useCreateDocumentTypeMutation();
  // Adding a type from this picker writes to the tenant-wide document type list,
  // so it needs the same permission the settings surface does.
  const canCreateType = hasPermission(
    currentUser,
    PERMISSIONS.SETTINGS_DOCUMENT_TYPES_WRITE,
  );
  const { data: documentTypes = [] } = useQuery(
    documentTypeQueryOptions.list(),
  );
  const [formData, setFormData] = useState<DocumentFormData>({
    title: "",
    docType: "",
    file: null,
    expiryDate: null,
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [typeSearchTerm, setTypeSearchTerm] = useState("");

  useEffect(() => {
    if (documentTypes.length > 0 && !formData.docType) {
      const defaultType =
        documentTypes.find((t) => t.name === "Other")?.name ??
        documentTypes[0].name;
      setFormData((prev) => ({ ...prev, docType: defaultType }));
    }
  }, [documentTypes, formData.docType]);

  const getFilenameWithoutExtension = (filename: string): string => {
    return filename.replace(/\.[^/.]+$/, "");
  };

  const setFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
      setFormErrors((prev) => ({
        ...prev,
        file: `File size exceeds the ${MAX_FILE_SIZE_MB}MB limit (selected file is ${sizeMb} MB).`,
      }));
      setFormData((prev) => ({ ...prev, file: null }));
      return;
    }
    const fileNameWithoutExtension = getFilenameWithoutExtension(file.name);
    setFormData((prev) => ({
      ...prev,
      file,
      title: prev.title.trim() ? prev.title : fileNameWithoutExtension,
    }));
    setFormErrors((prev) => ({ ...prev, file: "" }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) setFile(file);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) setFile(files[0]);
  };

  const handleDropAreaClick = () => {
    fileInputRef.current?.click();
  };

  const handleCreateType = async () => {
    const name = typeSearchTerm.trim();
    if (!name || !canCreateType) return;
    const result = await createTypeMutation.mutateAsync(name);
    if (result) {
      setFormData((prev) => ({ ...prev, docType: result.name }));
      setTypeSearchTerm("");
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      errors.title = "Title is required";
    }

    if (!formData.docType) {
      errors.docType = "Document type is required";
    }

    if (!formData.file) {
      errors.file = "File is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // The dialog stays open for the whole upload, so a second submit before the
    // first resolves would POST again and persist a duplicate document row.
    if (uploadMutation.isPending) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!patientId || !formData.file) {
      toast({
        title: "Error",
        description: "Missing required information",
        variant: "error",
      });
      return;
    }

    try {
      const result = await uploadMutation.mutateAsync({
        patientId,
        file: formData.file,
        title: formData.title,
        docType: formData.docType,
        expiryDate:
          isIdType && formData.expiryDate
            ? pickedDay(formData.expiryDate)
            : undefined,
      });

      if (result) {
        toast({
          title: "Success",
          description: "Document uploaded successfully",
          variant: "success",
        });

        onSuccess?.(result);
        onOpenChange(false);
        setFormData({
          title: "",
          docType: "",
          file: null,
          expiryDate: null,
        });
      }
    } catch (err) {
      const description = await parseError(err, "Failed to upload document");
      toast({
        title: "Error",
        description,
        variant: "error",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      docType: "",
      file: null,
      expiryDate: null,
    });
    setFormErrors({});
    setTypeSearchTerm("");
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const hasExactMatch = documentTypes.some(
    (t) => t.name.toLowerCase() === typeSearchTerm.trim().toLowerCase(),
  );

  // Exact match, consistent with the tenant gate and the doc_type=ID filter.
  const isIdType = formData.docType === ID_DOCUMENT_TYPE_NAME;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <form
            id="document-upload-form"
            onSubmit={onSubmit}
            className="space-y-4"
          >
            <div
              className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : formErrors.file
                    ? "border-red-500"
                    : formData.file
                      ? "border-green-500 bg-green-50"
                      : "hover:border-primary"
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleDropAreaClick}
            >
              {formData.file ? (
                <div className="space-y-2">
                  <FaUpload className="mx-auto h-8 w-8 text-status-success-text" />
                  <p className="text-status-success-text">
                    {formData.file.name}
                  </p>
                  <p className="text-body text-status-success-text">
                    File uploaded successfully
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <FaUpload className="mx-auto h-8 w-8 text-foreground-muted" />
                  <p className="text-foreground-secondary">
                    Drag and drop your file here, or click to browse
                  </p>
                  <p className="text-caption text-muted-foreground">
                    Supported formats: PDF, JPEG, PNG, Word (Max{" "}
                    {MAX_FILE_SIZE_MB}MB)
                  </p>
                </div>
              )}
            </div>

            <ScanControls onFile={setFile} />

            {formErrors.file && (
              <p className="text-body text-status-error-text">
                {formErrors.file}
              </p>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
            />

            <div className="space-y-1">
              <Label htmlFor="title">Document Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter document title"
                hasError={!!formErrors.title}
                errorMessage={formErrors.title}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="type">Document Type</Label>
              <SearchSelect
                value={formData.docType}
                onValueChange={(value) =>
                  setFormData({ ...formData, docType: value })
                }
                onOpenChange={(isOpen) => {
                  if (!isOpen) setTypeSearchTerm("");
                }}
              >
                <SearchSelectTrigger
                  hasError={!!formErrors.docType}
                  errorMessage={formErrors.docType}
                >
                  <SearchSelectValue placeholder="Select document type" />
                </SearchSelectTrigger>
                <SearchSelectContent
                  searchPlaceholder={
                    canCreateType
                      ? "Search or add new type..."
                      : "Search types..."
                  }
                  emptyMessage="No matching types."
                  onSearchChange={setTypeSearchTerm}
                  footerAction={
                    canCreateType && typeSearchTerm.trim() && !hasExactMatch ? (
                      <button
                        type="button"
                        onClick={handleCreateType}
                        disabled={createTypeMutation.isPending}
                        className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-body text-foreground-secondary transition-colors hover:bg-hover"
                      >
                        <FiPlus className="h-4 w-4" />
                        {createTypeMutation.isPending ? (
                          <span>{"Adding..."}</span>
                        ) : (
                          <span>{`Add "${typeSearchTerm.trim()}"`}</span>
                        )}
                      </button>
                    ) : null
                  }
                >
                  {documentTypes.map((type) => (
                    <SearchSelectItem key={type.id} value={type.name}>
                      {type.name}
                    </SearchSelectItem>
                  ))}
                </SearchSelectContent>
              </SearchSelect>
            </div>

            {isIdType && (
              <div className="space-y-1">
                <Label htmlFor="expiry">Expiry date (optional)</Label>
                <SmartDateInput
                  value={formData.expiryDate ?? undefined}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      expiryDate: date ?? null,
                    }))
                  }
                  direction="future"
                  side="top"
                  placeholder="Select expiry date"
                />
              </div>
            )}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="document-upload-form"
            isLoading={uploadMutation.isPending}
            loadingText="Uploading..."
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
